import { TextFieldWrapper } from "components/Wrappers/FormComponentsWrappers/TextFieldWrapper";
import { useGlobalContext } from "context/GlobalContext";
import React, { useEffect, useState } from "react";
import { useProductContext } from "../context";
import { fetchProductCategoriesApi } from "API/apis/master/getProductCategory";
import { message } from "constants/responses/message";
import { fetchUomApi } from "API/apis/master/getUom";
import { useParams } from "react-router-dom";
import { updateProductsApi } from "API/apis/product/updateProducts";
import { STATUS_CODES } from "constants/responses/statusCodes";
import { uploadFileApi } from "API/apis/files/uploadFile";
import { PageTitle } from "components/PageMeta/PageTitle";
import { pageName } from "constants/pageNames";
import { ProductTopBanner } from "../components/ProductTopBanner";
import { FormControlWrapper } from "components/Wrappers/FormComponentsWrappers/FormControlWrapper";
import {
  Button,
  Checkbox,
  Divider,
  FormLabel,
  TextField,
  Typography,
} from "@mui/material";
import { FlexBox } from "components/Wrappers/FlexBox/FlexBox";
import dayjs from "configs/dayjs.config";

import { fetchProductFromLotIdApi } from "API/apis/product/getProductLotFromLotId";
import { useNavigate } from "react-router-dom";
import { routeLinks } from "constants/paths";
import { DatePickerWrapper } from "components/DateTimePicker/DatePicker";
import { SingleSelect } from "components/Select/SingleSelect";

export const UpdateProduct = () => {
  const {
    setReloadRequired,
    previewProduct,
    currentProject,
    setCurrentProject,
  } = useProductContext();
  const [formData, setFormData] = useState(previewProduct);
  const { setError, setSuccess, setIsLoading, setIsInitializing } =
    useGlobalContext();
  const [categoryResponse, setCategoryResponse] = useState([]);
  const [uomResponse, setUomResponse] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  const { productLotId } = useParams();
  const [isDisabled, setIsDisabled] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setIsInitializing(true);
      try {
        const promises = [
          fetchProductFromLotIdApi(productLotId),
          fetchProductCategoriesApi(),
          fetchUomApi(),
        ];
        const [productDetailResponse, categoryResponse, uomResponse] =
          await Promise.all(promises);

        if (
          productDetailResponse.status !== 200 &&
          categoryResponse.status !== 200 &&
          uomResponse.status !== 200
        ) {
          throw new Error(message.error.generalFailure);
        }
        setFormData({
          ...productDetailResponse.data,
        });

        setCategoryResponse(
          categoryResponse.data.map((item) => ({
            option: item.id,
            value: item.childCategory,
          }))
        );
        setUomResponse(
          uomResponse.data.map((item) => ({
            option: item.key,
            value: item.value,
          }))
        );
        setCurrentProject(productDetailResponse.data.enquiryId);
        setIsDisabled(true);
        setIsInitializing(false);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsInitializing(false);
        setIsLoading(false);
        setReloadRequired(false);
      }
    };
    fetchData();
  }, []);

  const handleSelectChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleIsPCDRequired = (newValue) => {
    setFormData({
      ...formData,
      isPcdRequired: newValue,
    });
  };
  const handleChange = (e) => {
    setFormData({
      ...formData,
      enquiryId: Number(currentProject),
      [e.target.name]: e.target.value,
    });
  };
  const handleDeliveryDateChange = (newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        deliveryDate: newValue.toDate(),
      });
    }
  };
  const handlePartDeadlineChange = (newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        partDeadline: newValue.toDate(),
      });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };
  const handleUpdateUserSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      if (selectedFile) {
        const formFileData = new FormData();
        formFileData.append("file", selectedFile);
        const { data: fileUploadData, status: fileUploadStatus } =
          await uploadFileApi(formFileData);
        if (fileUploadStatus !== STATUS_CODES.SUCCESS) {
          throw new Error(fileUploadData.message);
        }
        const { data, status } = await updateProductsApi({
          ...formData,
          refImage: fileUploadData.filePath,
        });
        if (status !== STATUS_CODES.SUCCESS) {
          throw new Error(data.message);
        }
        setSuccess(message.success.updatedSuccessfully);
      } else {
        const { data, status } = await updateProductsApi({
          ...formData,
        });
        if (status !== STATUS_CODES.SUCCESS) {
          throw new Error(data.message);
        }
        setSuccess(data?.message);
      }
      setReloadRequired(true);
      navigate(routeLinks.projectManagementProject + "/" + currentProject);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setError(error.message);
    }
  };
  return (
    <>
      <PageTitle pageName={pageName.product.updateProduct} />
      <ProductTopBanner buttonType="back" />
      <form onSubmit={handleUpdateUserSubmit} encType="multipart/form-data">
        <Typography>Product Code : {formData.partDocCode}</Typography>

        <FormControlWrapper fullWidth>
          <Typography variant="h6">Upload Reference Image</Typography>
          <TextField
            type="file"
            name="image"
            onChange={handleFileChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{ accept: "image/*" }}
            fullWidth
          />
        </FormControlWrapper>
        <FormControlWrapper>
          <FormLabel>
            Is PCD Required
            <Checkbox
              aria-label="Is PCD Require"
              checked={formData?.isPcdRequired || false}
              value={formData.isPcdRequired}
              onChange={(e, newValue) => handleIsPCDRequired(newValue)}
            />
          </FormLabel>
        </FormControlWrapper>
        <TextFieldWrapper
          id="productName"
          name="productName"
          label="Product Name"
          variant="outlined"
          value={formData.productName || ""}
          fullWidth
          required
          onChange={handleChange}
        />
        <SingleSelect
          label="Product Category"
          name="productCategoryId"
          options={categoryResponse}
          handleSelectSingle={handleSelectChange}
          selectedOption={formData.productCategoryId || ""}
          isDisabled={isDisabled}
        />

        <FlexBox>
          <TextFieldWrapper
            id="productQuantity"
            name="productQuantity"
            label="Quantity"
            variant="outlined"
            value={formData.productQuantity || null}
            fullWidth
            required
            onChange={handleChange}
          />
          <SingleSelect
            label="UOM"
            name="productUom"
            options={uomResponse}
            handleSelectSingle={handleSelectChange}
            selectedOption={formData.productUom || ""}
            isDisabled={isDisabled}
          />
        </FlexBox>
        <FlexBox>
          <TextFieldWrapper
            id="costPrice"
            name="costPrice"
            label="Cost Price"
            variant="outlined"
            value={formData.costPrice || 0}
            fullWidth
            required
            onChange={handleChange}
            inputProps={{ min: 1 }}
          />
          <TextFieldWrapper
            id="sellingPrice"
            name="sellingPrice"
            label="Selling Price"
            variant="outlined"
            value={formData.sellingPrice || 0}
            fullWidth
            required
            onChange={handleChange}
            inputProps={{ min: 1 }}
          />
        </FlexBox>
        <DatePickerWrapper
          label="Delivery Date"
          value={dayjs(formData.deliveryDate) || null}
          onChange={(newValue) => handleDeliveryDateChange(dayjs(newValue))}
        />
        <TextFieldWrapper
          id="link"
          name="link"
          label="Link"
          variant="outlined"
          value={formData.link || ""}
          fullWidth
          onChange={handleChange}
        />
        <Typography variant="h6">Lot detail</Typography>
        <Divider />
        <TextFieldWrapper
          id="partCode"
          name="partCode"
          label="Part Name"
          variant="outlined"
          value={formData.partCode || ""}
          fullWidth
          onChange={handleChange}
        />
        <TextFieldWrapper
          id="partQuantity"
          name="partQuantity"
          label="Part Quantity"
          variant="outlined"
          value={formData.partQuantity || null}
          fullWidth
          required
          onChange={handleChange}
        />
        <DatePickerWrapper
          label="Delivery Date"
          value={dayjs(formData.partDeadline) || null}
          onChange={(newValue) => handlePartDeadlineChange(dayjs(newValue))}
        />

        <Button variant="contained" type="submit" fullWidth>
          Submit
        </Button>
      </form>
    </>
  );
};
